import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { X } from 'lucide-react';
import { useDialogFocus } from '../lib/dialog';
export function Modal({
  title,
  description,
  children,
  onClose,
  wide = false,
  footer,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(ref, onClose);
  // Panels use backdrop filters and page transitions. Their containing blocks must
  // not constrain a viewport dialog when the underlying page has been scrolled.
  return createPortal(
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        ref={ref}
        tabIndex={-1}
        className={`modal ${wide ? 'modal-wide' : ''} ${footer ? 'modal-with-footer' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        initial={{ y: 20, scale: 0.97 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 10, scale: 0.98 }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
      >
        <div className="modal-heading">
          <div>
            <h2>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <button className="icon-btn" aria-label="Close dialog" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        {footer ? (
          <>
            <div className="modal-scroll-body">{children}</div>
            {footer}
          </>
        ) : (
          children
        )}
      </motion.div>
    </motion.div>,
    document.body,
  );
}
